import { useEffect } from 'react';

import { useGetStockTransfersQuery } from 'data/__generated__';

import { DataTable } from 'components/DataTable';

import {
    stockTransfersColumns,
    type StockTransferRow,
    type StockTransfersFilter,
} from '../constants/stockTransfersColumns';

const PAGE_LIMIT = 30;

export const StockTransfersList = ({
    selectedKeys,
    onSelectionChange,
    onStockTransfersChange,
}: {
    selectedKeys: string[];
    onSelectionChange: (keys: string[]) => void;
    onStockTransfersChange: (stockTransfers: StockTransferRow[]) => void;
}) => {
    const {
        loading: stockTransfersLoading,
        data: stockTransfersData,
        error: stockTransfersError,
        fetchMore: fetchMoreStockTransfers,
        refetch: refetchStockTransfers,
    } = useGetStockTransfersQuery({
        variables: { offset: 0, limit: PAGE_LIMIT, filters: {} },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    });

    if (stockTransfersError || (!stockTransfersData && !stockTransfersLoading)) {
        throw new Error('Une erreur est survenue lors de la récupération des transferts de stock');
    }

    const hasMore = stockTransfersData?.stockTransfers?.hasMore ?? false;
    const stockTransfers = stockTransfersData?.stockTransfers?.stockTransfers ?? [];

    useEffect(() => {
        onStockTransfersChange(stockTransfers);
    }, [onStockTransfersChange, stockTransfers]);

    return (
        <DataTable<StockTransferRow, StockTransfersFilter>
            columns={stockTransfersColumns}
            data={stockTransfers}
            filterMode="server"
            getRowId={(row) => row._id}
            getRowLink={(row) => `/stock-transfer/${row._id}`}
            hasMore={hasMore}
            isLoading={stockTransfersLoading}
            refetch={async (vars) => {
                await refetchStockTransfers({ ...vars, offset: 0, limit: PAGE_LIMIT });
            }}
            fetchMore={async ({ variables }) => {
                await fetchMoreStockTransfers({ variables: { ...variables, limit: PAGE_LIMIT } });
            }}
            searchPlaceholder="Nom de site"
            searchFilterKey="searchValue"
            enableSelection
            selectedKeys={selectedKeys}
            onSelectionChange={onSelectionChange}
        />
    );
};
